import { createInstance } from "@optimizely/optimizely-sdk";
import type { Client } from "@optimizely/optimizely-sdk";
import { v4 as uuidv4 } from "uuid";
import { logger } from "observability";
import Cookies from "js-cookie";
import { mixpanelClient } from "analytics/mixpanel";
import {
  OPTIMIZELY_UUID_AGE,
  OPTIMIZELY_UUID_COOKIE_NAME,
  BASE_USER_ATTRIBUTES
} from "analytics/optimizely/config";
import customLogger from "analytics/optimizely/optimizelyLogger";
import axios from "axios";
import { isProductionEnv } from "config/utils";

export const getOptimizelyUUIDInBrowser = () => {
  let uuid = Cookies.get(OPTIMIZELY_UUID_COOKIE_NAME);
  if (!uuid) {
    uuid = uuidv4();
    Cookies.set(OPTIMIZELY_UUID_COOKIE_NAME, uuid, {
      expires: OPTIMIZELY_UUID_AGE
    });
    mixpanelClient.aliasId(uuid);
  }

  return uuid;
};

let OptimizelyInstance: Client | null;

const OptimizelyClient = {
  getInstance: () => OptimizelyInstance,
  init: async (datafile?: string) => {
    OptimizelyInstance = createInstance({
      logger: customLogger,
      datafile: datafile
    });
  },
  isFeatureEnabled: (
    featureFlag: string,
    uuid?: string,
    attributes?: Record<string, unknown>
  ) => {
    try {
      if (!uuid) {
        uuid = getOptimizelyUUIDInBrowser();
      }
      const isEnabled = OptimizelyInstance?.isFeatureEnabled(
        featureFlag,
        uuid,
        Object.assign(BASE_USER_ATTRIBUTES, attributes)
      );
      mixpanelClient.setSuperProperty({
        [`Feature: ${featureFlag}`]: isEnabled
      });
      return isEnabled;
    } catch (e) {
      logger.warn(`Could not retrieve feature flag ${featureFlag}`, e as Error);
    }
  }
};

export const getDataFile = async () => {
  const datafileUrl = isProductionEnv
    ? "https://exp.gemini.com/datafile.json"
    : "https://exp.sandbox.gemini.com/datafile.json";
  try {
    const datafile = await axios.get(datafileUrl, {});
    return datafile.data;
  } catch (e) {
    return null;
  }
};

// should only be invoked from getStaticProps
export const initOptimizely = async () => {
  const datafile = await getDataFile();
  await OptimizelyClient.init(datafile);
};

export const newOptimizely = async (datafile: any) => {
  return createInstance({
    logger: customLogger,
    datafile: datafile
  });
};

export default OptimizelyClient;
